/**
 * Presentation model for the site audit report.
 * Staff and the public /audit/:token page both render this. It does not
 * invent scores, and a missing or failed collection is a sentence, not a zero.
 * Layout reference: houtini-ai/seo-audit (Apache-2.0). No code was copied.
 */

import { figuresForPath, highlightSegments, sourceStatusLabel } from "./site-audit-facts.js";
import type { UnsupportedFigure } from "./site-audit-grounding.js";
import { readStoredLabTiming } from "./site-audit-speed.js";
import type { WpComponentStatus, WpStaffReport, WpStaffSection } from "./site-audit-wordpress.js";

export type ReportMode = "staff" | "client";

export type TextSegment = { text: string; flagged: boolean };

export type PresentedText = { text: string; segments: TextSegment[] };

export type Tone = "neutral" | "muted" | "problem" | "good" | "watch" | "act" | "urgent";

export type FigureTile = {
  id: string;
  label: string;
  /** value: a collected figure. empty: say emptyLabel, never a zero. */
  state: "value" | "empty";
  value: string | null;
  emptyLabel: string | null;
  note: string | null;
  tone: Tone;
};

export type FreshnessChip = {
  id: string;
  label: string;
  status: string;
  statusLabel: string;
  detail: string | null;
  tone: Tone;
};

export type CrawlIssue = {
  id: string;
  label: string;
  count: number | null;
  countLabel: string;
  severity: string;
  severityLabel: string;
  tone: Tone;
  /** Share of the largest count in this list. 0 to 1. */
  bar: number;
  sampleUrls: string[];
};

export type CrawlPage = {
  url: string;
  issues: string[];
  statusCode: number | null;
  loadTime: string | null;
  wordCount: string | null;
};

export type CrawlView =
  | {
      kind: "scored";
      cached: boolean;
      score: number | null;
      scoreLabel: string;
      /** Null when the crawl did not store a page total. Never a stand-in zero. */
      pages: number | null;
      pagesLabel: string;
      summary: string;
      /** Occurrences, summed from each issue's count. Not the number of categories. */
      criticalCount: number;
      warningCount: number;
      noticeCount: number;
      issues: CrawlIssue[];
      pagesWithIssues: CrawlPage[];
    }
  | { kind: "unreachable"; heading: string; detail: string | null }
  | { kind: "failed"; heading: string; detail: string | null }
  | { kind: "not_collected"; heading: string };

export type SpeedBand = "good" | "needs_improvement" | "poor";

export type SpeedMetric = {
  id: string;
  label: string;
  kind: "lab" | "field";
  state: "value" | "empty";
  value: string | null;
  band: SpeedBand | null;
  bandLabel: string | null;
  emptyLabel: string | null;
};

export type SpeedStrategy = {
  score: number | null;
  scoreLabel: string;
  metrics: SpeedMetric[];
  fieldNote: string;
};

export type SpeedFailure = { url: string; strategy: string; message: string };

export type SpeedView = {
  legacyFid: boolean;
  legacyNote: string | null;
  failures: SpeedFailure[];
  pages: {
    url: string;
    mobile: SpeedStrategy | null;
    desktop: SpeedStrategy | null;
  }[];
  emptyLabel: string | null;
};

export type KeywordRow = {
  keyword: string;
  url: string | null;
  position: number | null;
  positionLabel: string;
  /** 1 at position 1, shrinking toward 100. Null when position is unknown. */
  positionBar: number | null;
  volume: string;
  traffic: string;
};

export type CompetitorRow = {
  domain: string;
  prospect: boolean;
  authority: number | null;
  authorityLabel: string;
  traffic: number | null;
  trafficLabel: string;
  keywords: number | null;
  keywordsLabel: string;
  commonKeywords: number | null;
  commonKeywordsLabel: string;
};

export type GapRow = {
  keyword: string;
  volume: string;
  competitor: string;
  position: string;
};

export type SearchView = {
  keywords: KeywordRow[];
  keywordTotal: number;
  keywordEmpty: string | null;
  competitors: CompetitorRow[];
  competitorEmpty: string | null;
  competitiveAnalysis: PresentedText | null;
  gaps: GapRow[];
  gapTotal: number;
  gapEmpty: string | null;
};

export type AnchorRow = { anchor: string; count: number; countLabel: string; bar: number };

export type LinksView = {
  state: "value" | "empty";
  emptyLabel: string | null;
  backlinks: string | null;
  referringDomains: string | null;
  nofollowReferringDomains: string | null;
  brokenBacklinks: string | null;
  brokenTone: Tone;
  anchors: AnchorRow[];
};

export type CheckRow = {
  id: string;
  subject: string;
  verdict: string;
  verdictLabel: string;
  detail: string;
  tone: Tone;
};

export type CheckGroup = { id: string; label: string; rows: CheckRow[] };

export type FindingGroup = {
  severity: "critical" | "high" | "medium" | "low" | "other";
  label: string;
  tone: Tone;
  items: { title: PresentedText; detail: PresentedText; category: string }[];
};

export type ActionRow = {
  priority: string;
  finding: PresentedText;
  opportunity: PresentedText;
  action: PresentedText;
  impact: string;
};

export type QuickWin = { action: PresentedText; impact: PresentedText; effort: string };

export type SecurityComponent = {
  type: string;
  name: string;
  version: string | null;
  status: WpComponentStatus;
  statusLabel: string;
  tone: Tone;
  wording: string | null;
  findings: { title: string; wording: string; cve: string | null }[];
};

export type SecuritySegment = { id: "checked" | "version_hidden" | "unchecked"; label: string; count: number; share: number };

export type SecurityView =
  | { state: "missing"; message: string }
  | { state: "error"; message: string }
  | {
      state: "unscored";
      platform: string;
      message: string;
      bandLabel: string;
      warnings: string[];
      attribution: string | null;
    }
  | {
      state: "scored";
      band: string;
      bandLabel: string;
      tone: Tone;
      failCount: number;
      checked: number;
      versionHidden: number;
      unchecked: number;
      total: number;
      /** False when the feed is stale or anything was hidden or unchecked. */
      allClear: boolean;
      clearLabel: string;
      coverageLine: string;
      feedStale: boolean;
      segments: SecuritySegment[];
      components: SecurityComponent[];
      warnings: string[];
      coreVersion: string | null;
      phpVersion: string | null;
      attribution: string | null;
    };

export type PresentedReport = {
  mode: ReportMode;
  domain: string;
  collectedAt: string | null;
  verdict: PresentedText | null;
  freshness: FreshnessChip[];
  hero: {
    onPage: CrawlView;
    moz: FigureTile;
    traffic: FigureTile;
    keywords: FigureTile;
    speed: FigureTile;
    wordpress: FigureTile;
  };
  findings: FindingGroup[];
  actions: ActionRow[];
  quickWins: QuickWin[];
  nextSteps: PresentedText | null;
  crawl: CrawlView;
  speed: SpeedView;
  search: SearchView;
  links: LinksView;
  checks: CheckGroup[];
  security: SecurityView;
};

export type PresentAuditReportInput = {
  domain?: string | null;
  normalizedData?: unknown;
  report?: unknown;
  wordpress?: WpStaffSection | null;
  mode?: ReportMode;
  /** Used when technicalAudit.reach is absent. Ignored unless the crawl failed. */
  reach?: "unreachable" | null;
  jobComplete?: boolean;
};

const UNREACHABLE_HEADING = "The crawl couldn't reach this site.";
const FAILED_HEADING = "The crawl didn't finish.";
const NOT_COLLECTED_HEADING = "On-page crawl was not collected.";
const NO_SCORE = "No on-page score";
const LEGACY_FID_NOTE = "Lab Time to Interactive was saved under the old FID name. That number is not INP.";

const SOURCE_LABELS: { id: string; label: string }[] = [
  { id: "domainMetrics", label: "Domain metrics" },
  { id: "rankedKeywords", label: "Ranked keywords" },
  { id: "competitors", label: "Competitors" },
  { id: "keywordGap", label: "Keyword gap" },
  { id: "backlinks", label: "Backlinks" },
  { id: "onPage", label: "On-page crawl" },
  { id: "pagespeed", label: "PageSpeed" },
  { id: "moz", label: "Moz" },
  { id: "crux", label: "Field data" },
];

const FINDING_SEVERITY = ["critical", "high", "medium", "low", "other"] as const;

const CLIENT_CHECK_FAILED = "This check failed.";
const CLIENT_WARNINGS = "Some checks could not be finished.";

const CHECK_GROUPS: { id: string; label: string }[] = [
  { id: "soft-404-handling", label: "Soft 404" },
  { id: "host-variant-split", label: "Host variants" },
  { id: "ai-search-bot-blocked", label: "AI bots" },
];

const VERDICT_LABELS: Record<string, string> = {
  pass: "Pass",
  finding: "Finding",
  unknown: "Unknown",
  choice: "Choice",
  warning: "Warning",
  not_mentioned: "Not mentioned",
};

const STATUS_LABEL: Record<WpComponentStatus, string> = {
  vulnerable_no_fix: "Remove or replace",
  vulnerable_fix: "Update available",
  possibly_affected: "Version hidden",
  unchecked: "Not checked",
  ok: "No known issue",
};

const PRIORITY_ORDER = ["P0", "P1", "P2", "P3"];

export function presentAuditReport(input: PresentAuditReportInput): PresentedReport {
  const mode: ReportMode = input.mode === "client" ? "client" : "staff";
  const data = isRecord(input.normalizedData) ? input.normalizedData : {};
  const report = isRecord(input.report) ? input.report : null;
  const unsupported = mode === "staff" ? unsupportedFigures(report) : [];
  const sources = isRecord(data.sources) ? data.sources : null;
  const domain = text(input.domain) ?? text(data.domain) ?? "";
  const crawl = presentCrawl(data, input.reach ?? null, mode);
  const speed = presentSpeed(data, mode);
  const security = presentSecurity(input.wordpress ?? null, mode, input.jobComplete);

  return {
    mode,
    domain,
    collectedAt: text(data.collectedAt),
    verdict: presentField(report, "executiveSummary", "executiveSummary", unsupported),
    freshness: presentFreshness(sources, mode),
    hero: {
      onPage: crawl,
      moz: metricTile("moz", "Moz DA", sources, "moz", numberAt(data.domainMetrics, "authorityScore"), mode, {
        note: mozNote(data.domainMetrics),
      }),
      traffic: metricTile(
        "traffic",
        "Organic traffic",
        sources,
        "domainMetrics",
        numberAt(data.domainMetrics, "organicTrafficEstimate"),
        mode,
      ),
      keywords: metricTile(
        "keywords",
        "Organic keywords",
        sources,
        "domainMetrics",
        numberAt(data.domainMetrics, "organicKeywordCount"),
        mode,
        { note: positionOneNote(data.domainMetrics) },
      ),
      speed: speedTile(sources, speed, mode),
      wordpress: wordpressTile(security),
    },
    findings: presentFindings(report, unsupported),
    actions: presentActions(report, unsupported),
    quickWins: presentQuickWins(report, unsupported),
    nextSteps: presentField(report, "nextSteps", "nextSteps", unsupported),
    crawl,
    speed,
    search: presentSearch(data, sources, report, unsupported, domain),
    links: presentLinks(data, sources),
    checks: presentChecks(data.siteChecks),
    security,
  };
}

function presentCrawl(
  data: Record<string, unknown>,
  reach: "unreachable" | null,
  mode: ReportMode,
): CrawlView {
  const technical = isRecord(data.technicalAudit) ? data.technicalAudit : null;
  const status = technical ? text(technical.status) : null;
  if (!technical || status == null || status === "not_collected") {
    return { kind: "not_collected", heading: NOT_COLLECTED_HEADING };
  }
  if (status === "ok" || status === "cached") return scoredCrawl(technical, status === "cached");
  const marked = text(technical.reach) === "unreachable" || reach === "unreachable";
  const detail = mode === "client" ? null : plainReason(technical.failureReason);
  if (marked) return { kind: "unreachable", heading: UNREACHABLE_HEADING, detail };
  return { kind: "failed", heading: FAILED_HEADING, detail };
}

function scoredCrawl(technical: Record<string, unknown>, cached: boolean): CrawlView {
  const score = finite(technical.onPageScore);
  const pages = finite(technical.totalPagesCrawled);
  const scoreLabel = score == null ? NO_SCORE : formatNumber(score);
  const pagesLabel = pages == null ? "No figure" : formatCount(pages);
  const issues = crawlIssues(technical.errors);
  const cachedNote = cached ? ", cached" : "";
  const pagesClause = pages == null ? "" : `, ${pagesLabel} pages`;
  const head = score == null ? "No on-page score" : `On-page score ${scoreLabel} of 100`;
  return {
    kind: "scored",
    cached,
    score,
    scoreLabel,
    pages,
    pagesLabel,
    summary: `${head}${pagesClause}${cachedNote}`,
    criticalCount: occurrenceSum(issues, "critical"),
    warningCount: occurrenceSum(issues, "warning"),
    noticeCount: occurrenceSum(issues, "notice"),
    issues,
    pagesWithIssues: crawlPages(technical.pagesWithIssues),
  };
}

function occurrenceSum(issues: CrawlIssue[], severity: string): number {
  return issues.reduce((sum, issue) => {
    if (issue.severity !== severity || issue.count == null) return sum;
    return sum + issue.count;
  }, 0);
}

function crawlIssues(value: unknown): CrawlIssue[] {
  if (!Array.isArray(value)) return [];
  const rows = value.flatMap((item) => {
    if (!isRecord(item)) return [];
    const id = text(item.category) ?? "issue";
    const count = finite(item.count);
    const severity = text(item.severity) ?? "notice";
    const samples = Array.isArray(item.sampleUrls)
      ? item.sampleUrls.filter((url): url is string => typeof url === "string" && url.length > 0).slice(0, 2)
      : [];
    return [{
      id,
      label: labelFromKey(id),
      count,
      countLabel: count == null ? "No figure" : formatCount(count),
      severity,
      severityLabel: labelFromKey(severity),
      tone: severity === "critical" || severity === "warning" ? "problem" as const : "muted" as const,
      bar: 0,
      sampleUrls: samples,
    }];
  });
  const order = ["critical", "warning", "notice"];
  rows.sort((left, right) => order.indexOf(left.severity) - order.indexOf(right.severity));
  const max = rows.reduce((peak, row) => (row.count == null ? peak : Math.max(peak, row.count)), 0);
  return rows.map((row) => ({
    ...row,
    bar: row.count != null && max > 0 ? row.count / max : 0,
  }));
}

function crawlPages(value: unknown): CrawlPage[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    if (!isRecord(item)) return [];
    const url = text(item.url);
    if (!url) return [];
    const issues = Array.isArray(item.issues)
      ? item.issues.filter((issue): issue is string => typeof issue === "string" && issue.length > 0)
      : [];
    const statusCode = finite(item.statusCode);
    const load = finite(item.loadTime);
    const words = finite(item.wordCount);
    return [{
      url,
      issues,
      statusCode: statusCode != null && statusCode > 0 ? statusCode : null,
      loadTime: load == null ? null : `${Math.round(load)} ms`,
      wordCount: words == null ? null : formatCount(words),
    }];
  });
}

function presentSpeed(data: Record<string, unknown>, mode: ReportMode): SpeedView {
  const performance = isRecord(data.performance) ? data.performance : null;
  const pages = Array.isArray(performance?.pages) ? performance.pages : [];
  let legacyFid = false;
  const rows = pages.flatMap((page) => {
    if (!isRecord(page)) return [];
    const url = text(page.url) ?? "";
    const mobile = strategyView(page.mobile);
    const desktop = strategyView(page.desktop);
    if (mobile?.legacyFid || desktop?.legacyFid) legacyFid = true;
    if (!mobile && !desktop && !url) return [];
    return [{
      url,
      mobile: mobile?.view ?? null,
      desktop: desktop?.view ?? null,
    }];
  });
  const sources = isRecord(data.sources) ? data.sources : null;
  const pageSpeed = isRecord(sources?.pagespeed) ? sources.pagespeed : null;
  const failures = speedFailures(pageSpeed?.failures, mode);
  const anyStrategy = rows.some((row) => row.mobile || row.desktop);
  const status = pageSpeed ? text(pageSpeed.status) : null;
  let emptyLabel: string | null = null;
  if (!anyStrategy) {
    if (status === "failed") emptyLabel = "PageSpeed failed";
    else if (status === "not_collected" || rows.length === 0) emptyLabel = "Not collected";
  }
  return {
    legacyFid,
    legacyNote: legacyFid ? LEGACY_FID_NOTE : null,
    failures,
    pages: rows,
    emptyLabel,
  };
}

function strategyView(value: unknown): { view: SpeedStrategy; legacyFid: boolean } | null {
  if (!isRecord(value)) return null;
  const timing = readStoredLabTiming({ tti: finite(value.tti), fid: finite(value.fid) });
  const score = finite(value.performanceScore);
  const field = fieldView(value.field, timing.legacyFid || value.field == null);
  const metrics: SpeedMetric[] = [
    labMetric("lcp", "Lab LCP", finite(value.lcp), lcpBand, formatDuration),
    labMetric("tti", "Lab TTI", timing.tti, null, formatDuration),
    labMetric("tbt", "Lab TBT", finite(value.tbt), tbtBand, formatDuration),
    labMetric("cls", "Lab CLS", finite(value.cls), clsBand, formatCls),
  ];
  if (field.metrics) metrics.push(...field.metrics);
  return {
    legacyFid: timing.legacyFid,
    view: {
      score,
      scoreLabel: score == null ? "Not collected" : formatCount(Math.round(score)),
      metrics,
      fieldNote: field.note,
    },
  };
}

function fieldView(
  value: unknown,
  legacyMissing: boolean,
): { note: string; metrics: SpeedMetric[] | null } {
  if (!isRecord(value)) {
    return { note: legacyMissing ? "Field data was not stored for this run" : "No field data", metrics: null };
  }
  if (value.status === "ok") {
    const scope = value.scope === "origin" ? "Origin, not this URL" : "This URL";
    return {
      note: scope,
      metrics: [
        labMetric("fieldLcp", "Field LCP", finite(value.lcpMs), lcpBand, formatDuration, "field"),
        labMetric("fieldInp", "Field INP", finite(value.inpMs), inpBand, formatDuration, "field"),
        labMetric("fieldCls", "Field CLS", finite(value.cls), clsBand, formatCls, "field"),
      ],
    };
  }
  if (value.status === "no_field_data") return { note: "No field data", metrics: null };
  return { note: "Field data was not stored for this run", metrics: null };
}

function labMetric(
  id: string,
  label: string,
  value: number | null,
  bandOf: ((value: number) => SpeedBand) | null,
  format: (value: number) => string,
  kind: "lab" | "field" = "lab",
): SpeedMetric {
  if (value == null) {
    return { id, label, kind, state: "empty", value: null, band: null, bandLabel: null, emptyLabel: "Not collected" };
  }
  const band = bandOf ? bandOf(value) : null;
  return {
    id,
    label,
    kind,
    state: "value",
    value: format(value),
    band,
    bandLabel: band ? bandWord(band) : null,
    emptyLabel: null,
  };
}

function speedFailures(value: unknown, mode: ReportMode): SpeedFailure[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    if (!isRecord(item)) return [];
    const url = text(item.url) ?? "URL";
    const strategy = text(item.strategy) ?? "check";
    const message = mode === "client" ? CLIENT_CHECK_FAILED : plainReason(item.message) ?? "request failed";
    return [{ url, strategy, message }];
  });
}

function speedTile(sources: Record<string, unknown> | null, speed: SpeedView, mode: ReportMode): FigureTile {
  const status = sourceStatus(sources, "pagespeed");
  const first = speed.pages.find((page) => page.mobile || page.desktop) ?? null;
  const scored = speed.pages.flatMap((page) => {
    const picked = speedScore(page);
    return picked ? [{ page, picked }] : [];
  })[0] ?? null;
  if (scored && !sourceBlocks(status)) {
    return {
      id: "speed",
      label: "Speed",
      state: "value",
      value: scored.picked.scoreLabel,
      emptyLabel: null,
      note: scored.page.url ? `${scored.picked.strategy} · ${scored.page.url}` : scored.picked.strategy,
      tone: "neutral",
    };
  }
  if (status === "failed" || speed.emptyLabel === "PageSpeed failed") {
    const staffNote = speed.failures[0]?.message ?? sourceDetail(sources, "pagespeed");
    const note = mode === "client" ? (speed.failures.length > 0 ? CLIENT_CHECK_FAILED : null) : staffNote;
    return emptyTile("speed", "Speed", "PageSpeed failed", note, "problem");
  }
  return emptyTile("speed", "Speed", "Not collected", first?.url ?? null, "muted");
}

function speedScore(
  page: SpeedView["pages"][number] | null,
): { scoreLabel: string; strategy: "Mobile" | "Desktop" } | null {
  if (page?.mobile?.score != null) return { scoreLabel: page.mobile.scoreLabel, strategy: "Mobile" };
  if (page?.desktop?.score != null) return { scoreLabel: page.desktop.scoreLabel, strategy: "Desktop" };
  return null;
}

function presentSearch(
  data: Record<string, unknown>,
  sources: Record<string, unknown> | null,
  report: Record<string, unknown> | null,
  unsupported: UnsupportedFigure[],
  domain: string,
): SearchView {
  const keywordStatus = sourceStatus(sources, "rankedKeywords");
  const keywords = sourceBlocks(keywordStatus) ? [] : keywordRows(data.topKeywords);
  const competitorStatus = sourceStatus(sources, "competitors");
  const competitors = sourceBlocks(competitorStatus) ? [] : competitorRows(domain, data);
  const gapStatus = sourceStatus(sources, "keywordGap");
  const gaps = sourceBlocks(gapStatus) ? [] : gapRows(data.keywordGaps);
  const hasRival = competitors.some((row) => !row.prospect);
  return {
    keywords: keywords.slice(0, 10),
    keywordTotal: keywords.length,
    keywordEmpty: keywords.length > 0 ? null : emptyCopy(keywordStatus, "ranked keywords"),
    competitors,
    competitorEmpty: hasRival ? null : emptyCopy(competitorStatus, "competitors"),
    competitiveAnalysis: presentField(report, "competitiveAnalysis", "competitiveAnalysis", unsupported),
    gaps: gaps.slice(0, 10),
    gapTotal: gaps.length,
    gapEmpty: gaps.length > 0 ? null : emptyCopy(gapStatus, "keyword gap rows"),
  };
}

function keywordRows(value: unknown): KeywordRow[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    if (!isRecord(item)) return [];
    const position = finite(item.position);
    const known = position != null && position > 0 ? position : null;
    return [{
      keyword: text(item.keyword) ?? "—",
      url: text(item.url),
      position: known,
      positionLabel: known == null ? "—" : formatCount(known),
      positionBar: known == null ? null : (101 - Math.min(known, 100)) / 100,
      volume: formatMaybe(item.searchVolume),
      traffic: formatMaybe(item.trafficEstimate),
    }];
  });
}

function competitorRows(domain: string, data: Record<string, unknown>): CompetitorRow[] {
  const metrics = isRecord(data.domainMetrics) ? data.domainMetrics : {};
  const prospect = competitorRow(domain || "This site", true, {
    authority: finite(metrics.authorityScore),
    traffic: finite(metrics.organicTrafficEstimate),
    keywords: finite(metrics.organicKeywordCount),
    commonKeywords: null,
  });
  const rest = Array.isArray(data.competitorAnalysis)
    ? data.competitorAnalysis.flatMap((item) => {
        if (!isRecord(item)) return [];
        return [competitorRow(text(item.domain) ?? "—", false, {
          authority: finite(item.domainAuthority) ?? finite(item.authorityScore),
          traffic: finite(item.organicTrafficEstimate),
          keywords: finite(item.organicKeywordCount),
          commonKeywords: finite(item.commonKeywords),
        })];
      })
    : [];
  if (rest.length === 0 && prospect.authority == null && prospect.traffic == null && prospect.keywords == null) {
    return [];
  }
  return [prospect, ...rest.slice(0, 5)];
}

function competitorRow(
  domain: string,
  prospect: boolean,
  values: { authority: number | null; traffic: number | null; keywords: number | null; commonKeywords: number | null },
): CompetitorRow {
  return {
    domain,
    prospect,
    authority: values.authority,
    authorityLabel: values.authority == null ? "—" : formatNumber(values.authority),
    traffic: values.traffic,
    trafficLabel: values.traffic == null ? "—" : formatCount(values.traffic),
    keywords: values.keywords,
    keywordsLabel: values.keywords == null ? "—" : formatCount(values.keywords),
    commonKeywords: values.commonKeywords,
    commonKeywordsLabel: values.commonKeywords == null ? "—" : formatCount(values.commonKeywords),
  };
}

function gapRows(value: unknown): GapRow[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    if (!isRecord(item)) return [];
    const positions = Array.isArray(item.competitorPositions) ? item.competitorPositions : [];
    const first = isRecord(positions[0]) ? positions[0] : {};
    return [{
      keyword: text(item.keyword) ?? "—",
      volume: formatMaybe(item.searchVolume),
      competitor: text(first.domain) ?? "—",
      position: formatMaybe(first.position),
    }];
  });
}

function presentLinks(data: Record<string, unknown>, sources: Record<string, unknown> | null): LinksView {
  const status = sourceStatus(sources, "backlinks");
  const backlinks = isRecord(data.backlinks) ? data.backlinks : null;
  if (!backlinks || sourceBlocks(status)) {
    return {
      state: "empty",
      emptyLabel: emptyCopy(status, "backlinks"),
      backlinks: null,
      referringDomains: null,
      nofollowReferringDomains: null,
      brokenBacklinks: null,
      brokenTone: "muted",
      anchors: [],
    };
  }
  const broken = finite(backlinks.brokenBacklinks);
  const anchors = anchorRows(backlinks.topAnchors);
  return {
    state: "value",
    emptyLabel: null,
    backlinks: figureLabel(backlinks.totalBacklinks),
    referringDomains: figureLabel(backlinks.referringDomains),
    nofollowReferringDomains: figureLabel(backlinks.referringDomainsNofollow),
    brokenBacklinks: figureLabel(backlinks.brokenBacklinks),
    brokenTone: broken == null ? "muted" : broken > 0 ? "problem" : "neutral",
    anchors,
  };
}

function anchorRows(value: unknown): AnchorRow[] {
  if (!Array.isArray(value)) return [];
  const rows = value.flatMap((item) => {
    if (!isRecord(item)) return [];
    const anchor = text(item.anchor);
    if (!anchor) return [];
    const count = finite(item.count) ?? 0;
    return [{ anchor, count, countLabel: formatCount(count), bar: 0 }];
  });
  const max = rows.reduce((peak, row) => Math.max(peak, row.count), 0);
  return rows.map((row) => ({ ...row, bar: max > 0 ? row.count / max : 0 }));
}

function presentChecks(value: unknown): CheckGroup[] {
  if (!isRecord(value) || !Array.isArray(value.rows)) return [];
  const grouped = new Map<string, CheckRow[]>();
  for (const item of value.rows) {
    if (!isRecord(item) || typeof item.subject !== "string" || typeof item.detail !== "string") continue;
    const id = typeof item.id === "string" ? item.id : "site-checks";
    const verdict = typeof item.verdict === "string" ? item.verdict : "";
    const row: CheckRow = {
      id,
      subject: item.subject,
      verdict,
      verdictLabel: VERDICT_LABELS[verdict] ?? (verdict || "Unknown"),
      detail: item.detail,
      tone: verdict === "finding" || verdict === "warning" ? "problem" : "neutral",
    };
    const list = grouped.get(id) ?? [];
    list.push(row);
    grouped.set(id, list);
  }
  const known = CHECK_GROUPS.filter((group) => grouped.has(group.id)).map((group) => ({
    id: group.id,
    label: group.label,
    rows: grouped.get(group.id) ?? [],
  }));
  const rest = [...grouped.keys()]
    .filter((id) => !CHECK_GROUPS.some((group) => group.id === id))
    .map((id) => ({ id, label: labelFromKey(id), rows: grouped.get(id) ?? [] }));
  return [...known, ...rest];
}

function findingSeverity(value: unknown): (typeof FINDING_SEVERITY)[number] {
  const raw = typeof value === "string" ? value.trim().toLowerCase() : "";
  if (raw === "critical" || raw === "high" || raw === "medium" || raw === "low") return raw;
  return "other";
}

function presentFindings(report: Record<string, unknown> | null, unsupported: UnsupportedFigure[]): FindingGroup[] {
  const items = Array.isArray(report?.keyFindings) ? report.keyFindings : [];
  return FINDING_SEVERITY.flatMap((severity) => {
    const rows = items.flatMap((item, index) => {
      if (!isRecord(item) || findingSeverity(item.severity) !== severity) return [];
      return [{
        title: presentText(text(item.title) ?? "", `keyFindings[${index}].title`, unsupported),
        detail: presentText(text(item.detail) ?? "", `keyFindings[${index}].detail`, unsupported),
        category: text(item.category) ?? "",
      }];
    });
    if (rows.length === 0) return [];
    return [{
      severity,
      label: labelFromKey(severity),
      tone: severity === "critical" || severity === "high" ? "problem" as const : severity === "medium" ? "watch" as const : "muted" as const,
      items: rows,
    }];
  });
}

function presentActions(report: Record<string, unknown> | null, unsupported: UnsupportedFigure[]): ActionRow[] {
  const items = Array.isArray(report?.recommendations) ? report.recommendations : [];
  const rows = items.flatMap((item, index) => {
    if (!isRecord(item)) return [];
    return [{
      priority: text(item.priority) ?? "",
      finding: presentText(text(item.finding) ?? "", `recommendations[${index}].finding`, unsupported),
      opportunity: presentText(text(item.opportunity) ?? "", `recommendations[${index}].opportunity`, unsupported),
      action: presentText(text(item.action) ?? "", `recommendations[${index}].action`, unsupported),
      impact: text(item.impact) ?? "",
    }];
  });
  return rows.sort((left, right) => priorityRank(left.priority) - priorityRank(right.priority));
}

function recommendationActions(report: Record<string, unknown> | null): Set<string> {
  const items = Array.isArray(report?.recommendations) ? report.recommendations : [];
  const actions = new Set<string>();
  for (const item of items) {
    if (!isRecord(item)) continue;
    const action = text(item.action);
    if (action) actions.add(action.trim());
  }
  return actions;
}

function presentQuickWins(report: Record<string, unknown> | null, unsupported: UnsupportedFigure[]): QuickWin[] {
  const actions = recommendationActions(report);
  const items = Array.isArray(report?.quickWins) ? report.quickWins : [];
  return items.flatMap((item, index) => {
    if (!isRecord(item)) return [];
    const action = text(item.action) ?? "";
    if (actions.has(action.trim())) return [];
    return [{
      action: presentText(action, `quickWins[${index}].action`, unsupported),
      impact: presentText(text(item.impact) ?? "", `quickWins[${index}].impact`, unsupported),
      effort: text(item.effort) ?? "",
    }];
  });
}

function presentSecurity(section: WpStaffSection | null, mode: ReportMode, jobComplete: boolean | undefined): SecurityView {
  if (!section || section.state === "missing") {
    return {
      state: "missing",
      message: jobComplete === false
        ? "The WordPress check has not been stored yet."
        : "No WordPress check is stored for this run.",
    };
  }
  if (section.state === "error") return { state: "error", message: section.message };
  const report = section.report;
  if (!report.scored) {
    return {
      state: "unscored",
      platform: report.platform,
      message: report.sectionMessage,
      bandLabel: report.bandLabel,
      warnings: visibleWarnings(report.warnings, mode),
      attribution: visibleAttribution(report.attribution, mode),
    };
  }
  return scoredSecurity(report, mode);
}

function scoredSecurity(report: WpStaffReport, mode: ReportMode): SecurityView {
  const checked = tileCount(report, "no_fix") + tileCount(report, "fix") + tileCount(report, "no_known_issue");
  const versionHidden = tileCount(report, "hidden_version");
  const unchecked = tileCount(report, "unchecked");
  const total = checked + versionHidden + unchecked;
  const storedClear = report.tiles.find((tile) => tile.id === "no_known_issue")?.label ?? "No known issue";
  const clearLabel = report.feedStale ? "Not marked clear" : storedClear;
  const segments: SecuritySegment[] = total === 0 ? [] : [
    { id: "checked", label: "Checked", count: checked, share: checked / total },
    { id: "version_hidden", label: "Version hidden", count: versionHidden, share: versionHidden / total },
    { id: "unchecked", label: "Not checked", count: unchecked, share: unchecked / total },
  ];
  return {
    state: "scored",
    band: report.band,
    bandLabel: report.bandLabel,
    tone: bandTone(report.band),
    failCount: report.failCount,
    checked,
    versionHidden,
    unchecked,
    total,
    allClear: !report.feedStale && report.failCount === 0 && versionHidden === 0 && unchecked === 0 && total > 0,
    clearLabel,
    coverageLine: report.coverageLine,
    feedStale: report.feedStale,
    segments,
    components: report.components
      .filter((component) => component.status !== "ok")
      .map((component) => ({
        type: component.type,
        name: component.name,
        version: component.version,
        status: component.status,
        statusLabel: STATUS_LABEL[component.status],
        tone: component.status === "vulnerable_no_fix" || component.status === "vulnerable_fix" ? "problem" : "muted",
        wording: component.wording,
        findings: component.findings.map((finding) => ({
          title: finding.title,
          wording: finding.wording,
          cve: mode === "client" ? null : finding.cve,
        })),
      })),
    warnings: visibleWarnings(report.warnings, mode),
    coreVersion: report.coreVersion,
    phpVersion: report.phpVersion,
    attribution: visibleAttribution(report.attribution, mode),
  };
}

function visibleWarnings(warnings: string[], mode: ReportMode): string[] {
  if (mode !== "client") return warnings;
  return warnings.length > 0 ? [CLIENT_WARNINGS] : [];
}

function visibleAttribution(attribution: string | null | undefined, mode: ReportMode): string | null {
  if (mode === "client") return null;
  return attribution || null;
}

function wordpressTile(security: SecurityView): FigureTile {
  if (security.state === "missing") return emptyTile("wordpress", "WordPress", "Not stored", null, "muted");
  if (security.state === "error") return emptyTile("wordpress", "WordPress", "Could not load", null, "problem");
  if (security.state === "unscored") {
    const label = security.platform === "not_wordpress" ? "Not a WordPress site" : security.bandLabel;
    return emptyTile("wordpress", "WordPress", label, null, "muted");
  }
  const note = security.failCount === 1 ? "1 fail" : `${security.failCount} fails`;
  return {
    id: "wordpress",
    label: "WordPress",
    state: "value",
    value: security.bandLabel,
    emptyLabel: null,
    note,
    tone: security.tone,
  };
}

function presentFreshness(sources: Record<string, unknown> | null, mode: ReportMode): FreshnessChip[] {
  if (!sources) return [];
  return SOURCE_LABELS.flatMap((item) => {
    const value = sources[item.id];
    if (!isRecord(value) || typeof value.status !== "string") return [];
    const status = value.status;
    return [{
      id: item.id,
      label: item.label,
      status,
      statusLabel: sourceStatusLabel(status),
      detail: mode === "client" ? null : plainReason(value.detail),
      tone: status === "failed" ? "problem" as const : status === "ok" || status === "cached" ? "neutral" as const : "muted" as const,
    }];
  });
}

function metricTile(
  id: string,
  label: string,
  sources: Record<string, unknown> | null,
  sourceId: string,
  value: number | null,
  mode: ReportMode,
  extra?: { note?: string | null },
): FigureTile {
  const status = sourceStatus(sources, sourceId);
  if (status === "failed") {
    return emptyTile(id, label, "Failed", mode === "client" ? null : sourceDetail(sources, sourceId), "problem");
  }
  if (status === "not_collected") return emptyTile(id, label, "Not collected", null, "muted");
  if (value == null) return emptyTile(id, label, "No figure", null, "muted");
  return {
    id,
    label,
    state: "value",
    value: formatNumber(value),
    emptyLabel: null,
    note: extra?.note ?? null,
    tone: "neutral",
  };
}

function emptyTile(id: string, label: string, emptyLabel: string, note: string | null, tone: Tone): FigureTile {
  return { id, label, state: "empty", value: null, emptyLabel, note, tone };
}

function presentField(
  report: Record<string, unknown> | null,
  key: string,
  path: string,
  unsupported: UnsupportedFigure[],
): PresentedText | null {
  if (!report) return null;
  const value = text(report[key]);
  if (value == null) return null;
  return presentText(value, path, unsupported);
}

function presentText(textValue: string, path: string, unsupported: UnsupportedFigure[]): PresentedText {
  return { text: textValue, segments: highlightSegments(textValue, figuresForPath(unsupported, path)) };
}

function unsupportedFigures(report: Record<string, unknown> | null): UnsupportedFigure[] {
  const grounding = isRecord(report?.grounding) ? report.grounding : null;
  if (!Array.isArray(grounding?.unsupported)) return [];
  return grounding.unsupported.flatMap((item) => {
    if (!isRecord(item) || typeof item.path !== "string" || typeof item.figure !== "string") return [];
    return [{ path: item.path, figure: item.figure }];
  });
}

function mozNote(metrics: unknown): string | null {
  if (!isRecord(metrics)) return null;
  const page = finite(metrics.pageAuthority);
  const spam = finite(metrics.spamScore);
  const parts = [
    page == null ? null : `Page authority ${formatNumber(page)}`,
    spam == null ? null : `Spam score ${formatNumber(spam)}`,
  ].filter((part): part is string => part != null);
  return parts.length > 0 ? parts.join(" · ") : null;
}

function positionOneNote(metrics: unknown): string | null {
  if (!isRecord(metrics)) return null;
  const value = finite(metrics.keywordsInPosition1);
  return value == null ? null : `${formatCount(value)} in position 1`;
}

function sourceStatus(sources: Record<string, unknown> | null, id: string): string | null {
  if (!sources) return null;
  const value = sources[id];
  return isRecord(value) && typeof value.status === "string" ? value.status : null;
}

function sourceDetail(sources: Record<string, unknown> | null, id: string): string | null {
  if (!sources) return null;
  const value = sources[id];
  return isRecord(value) ? plainReason(value.detail) : null;
}

function sourceBlocks(status: string | null): boolean {
  return status === "failed" || status === "not_collected";
}

function emptyCopy(status: string | null, noun: string): string {
  if (status === "failed") return `${capitalise(noun)} failed and are not listed.`;
  if (status === "not_collected") return `${capitalise(noun)} were not collected.`;
  return `No ${noun} in this run.`;
}

function tileCount(report: WpStaffReport, id: WpStaffReport["tiles"][number]["id"]): number {
  return report.tiles.find((tile) => tile.id === id)?.count ?? 0;
}

function bandTone(band: string): Tone {
  if (band === "clear") return "good";
  if (band === "watch") return "watch";
  if (band === "act") return "act";
  if (band === "urgent") return "urgent";
  return "muted";
}

function priorityRank(priority: string): number {
  const index = PRIORITY_ORDER.indexOf(priority);
  return index === -1 ? PRIORITY_ORDER.length : index;
}

function lcpBand(ms: number): SpeedBand {
  if (ms <= 2500) return "good";
  if (ms <= 4000) return "needs_improvement";
  return "poor";
}

function tbtBand(ms: number): SpeedBand {
  if (ms <= 200) return "good";
  if (ms <= 600) return "needs_improvement";
  return "poor";
}

function inpBand(ms: number): SpeedBand {
  if (ms <= 200) return "good";
  if (ms <= 500) return "needs_improvement";
  return "poor";
}

function clsBand(value: number): SpeedBand {
  if (value <= 0.1) return "good";
  if (value <= 0.25) return "needs_improvement";
  return "poor";
}

function bandWord(band: SpeedBand): string {
  if (band === "good") return "Good";
  if (band === "needs_improvement") return "Needs improvement";
  return "Poor";
}

function formatDuration(ms: number): string {
  if (Math.abs(ms) >= 1000) {
    const seconds = Math.round((ms / 1000) * 10) / 10;
    return `${seconds} s`;
  }
  return `${Math.round(ms)} ms`;
}

function formatCls(value: number): string {
  return String(Math.round(value * 1000) / 1000);
}

function formatCount(value: number): string {
  return Math.round(value).toLocaleString("en-GB");
}

function formatNumber(value: number): string {
  return (Math.round(value * 10) / 10).toLocaleString("en-GB");
}

function figureLabel(value: unknown): string {
  const number = finite(value);
  return number == null ? "No figure" : formatCount(number);
}

function formatMaybe(value: unknown): string {
  const number = finite(value);
  return number == null ? "—" : formatNumber(number);
}

function labelFromKey(value: string): string {
  const spaced = value.replaceAll("_", " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function capitalise(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function plainReason(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const line = value.split("\n")[0]?.trim() ?? "";
  if (!line) return null;
  if (/node_modules|\bat\s+\S+:\d+/.test(line)) return null;
  return line.length > 240 ? line.slice(0, 240) : line;
}

function numberAt(value: unknown, key: string): number | null {
  return isRecord(value) ? finite(value[key]) : null;
}

function finite(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function text(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === "object" && !Array.isArray(value);
}
